import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Checkbox, Col, Space, Tooltip } from 'antd';
import { useField, useFormikContext } from 'formik';

import { getDaysOfWeekOptions } from 'appConstants';
import StyledFormItem from 'components/Form/StyledFormItem';
import { FlexContainer } from 'components/Layout/common';
import TimezonePicker from 'components/TimezonePicker';
import { FItemSelect, FItemTimePicker } from 'components/formik';
import { makeSelectTenantTimezone } from 'containers/App/selectors';
import { TRuleCreatePage } from 'containers/Rule/i18n/TModules';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import { TUtilsTypes } from 'utils/globalTranslation/TModules';

export interface RuleTriggerDayTimeItem {
  attribute: 'transaction_date' | 'transaction_time';
  operator: 'day_of_week' | 'less_than_or_equal_to' | 'greater_than_or_equal_to';
  selected_values: string[] | string;
}
export const tempRuleTriggerCheckBoxKey = 'temp.rule_trigger_checked';

const selectFieldName = `rule_trigger_days[0].selected_values`;
const formName = `rule_trigger_days[1].selected_values`;
const toName = `rule_trigger_days[2].selected_values`;

export const defaultValues: RuleTriggerDayTimeItem[] = [
  { attribute: 'transaction_date', operator: 'day_of_week', selected_values: [] },
  { attribute: 'transaction_time', operator: 'greater_than_or_equal_to', selected_values: '' },
  { attribute: 'transaction_time', operator: 'less_than_or_equal_to', selected_values: '' }
];

const monthOptions = getDaysOfWeekOptions();

const RuleTriggerDaySelect = () => {
  const { t } = useMultiLanguageTranslation();
  const [checkboxField] = useField<boolean | undefined>(tempRuleTriggerCheckBoxKey);
  const isFieldsDisabled = !checkboxField.value;
  return (
    <FItemSelect
      label={t(`${TRuleCreatePage}.RuleSetup.TriggerDays`)}
      name={selectFieldName}
      placeholder={t(`${TRuleCreatePage}.RuleSetup.SelectDays`)}
      allowClear
      mode="multiple"
      disabled={isFieldsDisabled}
      options={monthOptions.map(option => ({
        ...option,
        label: `${t(`${TUtilsTypes}.${option.label}`)}`
      }))}
      showArrow
      showSearch
      formItemProps={{ style: { marginBottom: 0 } }}
      maxTagCount="responsive"
      maxTagPlaceholder={omittedValues => (
        <Tooltip
          overlayStyle={{ pointerEvents: 'none' }}
          title={omittedValues.map(({ label }) => label).join(', ')}
        >
          <span>{t(`${TRuleCreatePage}.RuleSetup.HoverMe`)}</span>
        </Tooltip>
      )}
    />
  );
};
const RuleTriggerTimeSelect = () => {
  const { t } = useMultiLanguageTranslation();
  const [checkboxField] = useField<boolean | undefined>(tempRuleTriggerCheckBoxKey);
  const isFieldsDisabled = !checkboxField.value;
  const tenantTimezone = useSelector(makeSelectTenantTimezone()) || 'UTC';
  return (
    <FlexContainer noWrap hasSpaces>
      <Col style={{ flex: 1 }}>
        <FItemTimePicker
          label={t(`${TRuleCreatePage}.RuleSetup.From`)}
          name={formName}
          storedValueFormat="HH:mm:ss"
          placeholder={t(`${TRuleCreatePage}.RuleSetup.SelectTime`)}
          disabled={isFieldsDisabled}
          formItemProps={{ style: { marginBottom: 0 } }}
          style={{ width: '100%' }}
        />
      </Col>
      <Col style={{ flex: 1 }}>
        <FItemTimePicker
          label={t(`${TRuleCreatePage}.RuleSetup.To`)}
          name={toName}
          storedValueFormat="HH:mm:ss"
          placeholder={t(`${TRuleCreatePage}.RuleSetup.SelectTime`)}
          disabled={isFieldsDisabled}
          formItemProps={{ style: { marginBottom: 0 } }}
          style={{ width: '100%' }}
        />
      </Col>
      <Col style={{ flex: 1 }}>
        <TimezonePicker value={tenantTimezone} disabled={true} />
      </Col>
    </FlexContainer>
  );
};
const RuleTriggerDayTime = ({
  name = 'rule_trigger_days',
  showLabel = true
}: {
  name?: string;
  showLabel?: boolean;
}) => {
  const { t } = useMultiLanguageTranslation();
  const formik = useFormikContext();
  const [field, meta] = useField<RuleTriggerDayTimeItem[] | undefined>(name);
  const [checkboxField] = useField<boolean | undefined>(tempRuleTriggerCheckBoxKey);
  const isValid = meta.touched && !meta.error;

  useEffect(() => {
    if (field.value === undefined) {
      formik.setFieldValue(name, defaultValues);
    }
  }, [field.value, formik, name]);

  useEffect(() => {
    if (checkboxField.value === undefined) {
      formik.setFieldValue(tempRuleTriggerCheckBoxKey, false);
    }
  }, [checkboxField.value, formik]);

  const resetError = () => {
    formik.setFieldError(formName, undefined);
    formik.setFieldError(toName, undefined);
    formik.setFieldError(selectFieldName, undefined);
    formik.setFieldTouched(formName, false);
    formik.setFieldTouched(toName, false);
    formik.setFieldTouched(selectFieldName, false);
  };
  return (
    <StyledFormItem
      label={showLabel ? t(`${TRuleCreatePage}.RuleSetup.RuleTriggerDays`) : ''}
      margin="0"
      labelCol={{ xs: { span: 24 }, sm: showLabel ? { span: 6 } : { span: 0 } }}
      wrapperCol={{ xs: { span: 24 }, sm: showLabel ? { span: 12 } : { span: 24 } }}
      required={checkboxField.value}
      style={{ marginTop: '1rem' }}
      validateStatus={meta.error && meta.touched ? 'error' : undefined}
      hasFeedback={isValid}
      data-testid="rule-trigger-days-field"
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Checkbox
          checked={checkboxField.value}
          onChange={e => {
            if (!e.target.checked) {
              formik.setFieldValue(name, defaultValues);
              resetError();
            } else {
              formik.setFieldValue(formName, '00:00:00');
              formik.setFieldValue(toName, '23:59:00');
            }
            formik.setFieldValue(tempRuleTriggerCheckBoxKey, e.target.checked);
          }}
        >
          {t(`${TRuleCreatePage}.RuleSetup.TriggerOnSpecificDays`)}
        </Checkbox>
        <RuleTriggerDaySelect />
        <RuleTriggerTimeSelect />
      </Space>
    </StyledFormItem>
  );
};

export default RuleTriggerDayTime;
